/**
 * SavedInvestigationsPanel — list of snapshots for a given company.
 * Shown inside the Pipeline row expansion.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import TagChips from './TagChips';
import VisibilityToggle, { VisibilityBadge } from './VisibilityToggle';
import { formatBackendDetail } from '../lib/apiErrors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SavedInvestigationsPanel({ companyId, onOpen }) {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tagFilter, setTagFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ company_id: companyId });
      if (tagFilter) qs.set('tag', tagFilter);
      const res = await fetch(`${API_URL}/api/investigations?${qs}`);
      if (!res.ok) { setItems([]); return; }
      const data = await res.json();
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await load();
      if (cancelled) setItems([]);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, tagFilter]);

  // Distinct tags present in the currently-loaded set (for the filter chips)
  const uniqueTags = Array.from(new Set(items.flatMap((i) => i.tags || []))).sort();

  return (
    <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 8 }}
         data-testid={`saved-investigations-${companyId}`}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6,
      }}>
        <span style={{
          fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
          letterSpacing: '0.04em', color: 'var(--text-secondary)',
        }}>
          Saved investigations
        </span>
        <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>
          {loading ? 'loading…' : `${items.length} snapshot${items.length === 1 ? '' : 's'}${tagFilter ? ` · #${tagFilter}` : ''}`}
        </span>
      </div>

      {/* Tag filter chips (only shown when there are tags present on 2+ snapshots) */}
      {(uniqueTags.length > 0 || tagFilter) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}
             data-testid={`saved-tag-filter-${companyId}`}>
          {tagFilter && (
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setTagFilter('')}
              data-testid={`saved-tag-filter-clear-${companyId}`}
              style={{ fontSize: 10, padding: '2px 8px' }}
            >
              Clear filter (#{tagFilter})
            </button>
          )}
          {!tagFilter && uniqueTags.map((t) => (
            <button
              key={t}
              onClick={() => setTagFilter(t)}
              data-testid={`saved-tag-filter-chip-${t}`}
              style={{
                fontSize: 10, padding: '2px 8px', borderRadius: 12,
                background: '#eef2ff', color: '#3730a3', fontWeight: 600,
                border: 'none', cursor: 'pointer',
              }}
            >
              #{t}
            </button>
          ))}
        </div>
      )}

      {!loading && items.length === 0 && (
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', padding: '6px 0', fontStyle: 'italic' }}
             data-testid={`saved-investigations-empty-${companyId}`}>
          {tagFilter
            ? `No snapshots tagged #${tagFilter} for this company.`
            : 'No saved investigations yet. Run an Analysis then click "Save Investigation" on Fix or Fit to preserve the exact state.'}
        </div>
      )}

      {items.map((inv) => {
        const isMine = me?.id && inv.created_by === me.id;
        const changeVis = async (next) => {
          const r = await fetch(`${API_URL}/api/investigations/${inv.id}/visibility`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ visibility: next }),
          });
          if (!r.ok) {
            const d = await r.json().catch(() => ({}));
            throw new Error(formatBackendDetail(d.detail) || `HTTP ${r.status}`);
          }
          // Optimistic local update — avoid a full reload flicker.
          setItems((cur) => cur.map((x) => x.id === inv.id ? { ...x, visibility: next } : x));
        };
        return (
        <div key={inv.id} className="saved-investigation-card" data-testid={`saved-investigation-${inv.id}`}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>
              {inv.headline_summary || inv.scope_label || inv.analysis_lens || 'Investigation'}
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 2 }}>
              {new Date(inv.created_at).toLocaleString()}
              {inv.created_by_email ? ` · ${inv.created_by_email}` : ''}
              {inv.analysis_lens ? ` · lens: ${inv.analysis_lens}` : ''}
              {inv.scope_label ? ` · ${inv.scope_label}` : ''}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
              <VisibilityToggle
                visibility={inv.visibility}
                canEdit={isMine}
                onChange={changeVis}
                compact
                testid={`saved-inv-visibility-${inv.id}`}
              />
              {inv.profile_sector && (
                <span
                  className="badge"
                  data-testid="saved-investigation-profile-chip"
                  title={`Sector intelligence profile: ${inv.profile_sector}`}
                  style={{
                    fontSize: 9,
                    background: 'rgba(186, 151, 123, 0.15)',
                    color: '#7c5a40',
                    padding: '2px 6px',
                    borderRadius: 4,
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <span>PROFILE</span>
                  <span style={{ opacity: 0.55 }}>·</span>
                  <span style={{ letterSpacing: 0, fontWeight: 600, textTransform: 'none' }}>
                    {inv.profile_sector}
                  </span>
                </span>
              )}
              {(inv.tags && inv.tags.length > 0) && (
                <TagChips
                  tags={inv.tags}
                  compact
                  onTagClick={(t) => setTagFilter(t)}
                  testidPrefix={`saved-inv-tag-${inv.id}`}
                />
              )}
            </div>
          </div>
          {inv.overall_risk_score != null && (
            <span className="badge badge-type" style={{ fontSize: 10 }}>
              Risk {Math.round(inv.overall_risk_score)}
            </span>
          )}
          {inv.has_fixer && <span className="badge badge-type" style={{ fontSize: 9 }}>FIX</span>}
          {inv.has_fit && <span className="badge badge-type" style={{ fontSize: 9 }}>FIT</span>}
          <button
            className="btn btn-outline btn-sm"
            style={{ fontSize: 10, padding: '2px 8px' }}
            onClick={() => onOpen && onOpen(inv.id)}
            data-testid={`open-investigation-${inv.id}`}
          >
            Open
          </button>
        </div>
        );
      })}
    </div>
  );
}
